import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  CheckoutState,
  updateCheckoutState,
  BillingAddress,
} from "../checkout";
import {
  failCountryValidation,
  failStateValidation,
  returnedInvalidAddress,
  suggestAddress,
  userAcceptedSuggestedAddress,
} from "./action-creators";

export interface CheckoutPhysicalAddressState {
  address: BillingAddress;
  isSameAsBillingAddress: boolean;
  // TODO (cory): Modal state does not belong in Redux. But the checkout flow is very complicated...
  showCountryInvalidModal: boolean;
  showStateInvalidModal: boolean;
  showSuggestedAddressModal: boolean;
  showInvalidAddressModal: boolean;
  // The corrected address returned by the validation API
  suggestedAddress: Partial<BillingAddress>;
  // Whether or not to show an error message due to failed TR validation
  addressInvalid: boolean;
  // Whether the user accepted one of the choices in the Suggested Address modal. ie, they did not
  // go back to editing
  acceptedSuggestedAddress: boolean;
}

const initialState: CheckoutPhysicalAddressState = {
  address: {} as BillingAddress,
  isSameAsBillingAddress: false,
  showCountryInvalidModal: false,
  showStateInvalidModal: false,
  showSuggestedAddressModal: false,
  showInvalidAddressModal: false,
  suggestedAddress: {},
  addressInvalid: false,
  acceptedSuggestedAddress: false,
};

const checkoutPhysicalAddressSlice = createSlice({
  name: "checkoutPhysicalAddress",
  initialState,
  reducers: {
    setAddress: (state, action: PayloadAction<BillingAddress>) => ({
      ...state,
      address: action.payload,
      // When the user changes the address, clear the error message
      addressInvalid: false,
      suggestedAddress: {},
    }),
    toggleSameAsBillingAddress: (state, action: PayloadAction<boolean>) => ({
      ...state,
      isSameAsBillingAddress: action.payload,
    }),
    closeModal: (state) => ({
      ...state,
      showCountryInvalidModal: false,
      showStateInvalidModal: false,
      showSuggestedAddressModal: false,
      showInvalidAddressModal: false,
    }),
  },
  extraReducers: (builder) => {
    builder
      .addCase(failCountryValidation, (state) => ({
        ...state,
        showCountryInvalidModal: true,
      }))
      .addCase(failStateValidation, (state) => ({
        ...state,
        showStateInvalidModal: true,
      }))
      .addCase(userAcceptedSuggestedAddress, (state, action) => {
        const ret = { ...state };
        if (!action.payload.isSameAsBillingAddress) {
          ret.address = {
            ...state.address,
            ...action.payload.suggestedAddress,
          };
        }
        ret.showSuggestedAddressModal = false;
        ret.addressInvalid = false;
        ret.acceptedSuggestedAddress = true;
        return ret;
      })
      .addCase(
        suggestAddress,
        (state, action: PayloadAction<Partial<BillingAddress>>) => ({
          ...state,
          showSuggestedAddressModal: true,
          suggestedAddress: action.payload,
          addressInvalid: true,
          acceptedSuggestedAddress: false,
        })
      )
      .addCase(returnedInvalidAddress, (state) => ({
        ...state,
        showInvalidAddressModal: true,
        suggestedAddress: {},
        addressInvalid: true,
      }))
      .addCase(
        updateCheckoutState,
        // If their physical address is the same as billing address, clear errors when they update
        // billing address
        (state, action: PayloadAction<Partial<CheckoutState>>) => {
          if (!state.isSameAsBillingAddress) {
            return state;
          }
          if (!action.payload.billingAddress) {
            return state;
          }
          return {
            ...state,
            addressInvalid: false,
            suggestedAddress: {},
          };
        }
      );
  },
});

export const { setAddress, toggleSameAsBillingAddress, closeModal } =
  checkoutPhysicalAddressSlice.actions;

export default checkoutPhysicalAddressSlice;
